// Corpo de e-mail dentro da thread (Fase 7).
//
// Um e-mail não é uma mensagem de WhatsApp: tem assunto, tem cópia, e o corpo é
// HTML escrito por um estranho. Este componente existe para mostrar isso sem
// deixar o estranho executar nada.
//
// ⚠️ O `sandbox` do iframe é a defesa principal, e o atributo está escrito
// vazio DE PROPÓSITO. Vazio significa "todas as restrições ligadas": sem
// scripts, sem formulários, sem navegação da janela de cima, e com origem
// opaca — o conteúdo não enxerga a sessão do VigorHub nem se quisesse.
//
// NÃO acrescente `allow-scripts` aqui. Nenhum e-mail legítimo precisa disso, e
// acrescentar reabre exatamente o buraco que este arquivo fecha. Se algum dia
// algo "não funcionar sem", a resposta certa é não funcionar.

import React, { useMemo, useState } from 'react';
import { ImageOff, Mail } from 'lucide-react';
import { montarDocumento, temImagemRemota } from '../emailSeguro';

interface Props {
  assunto?: string | null;
  /** HTML original, como veio do provider. Nunca foi sanitizado no backend. */
  html?: string | null;
  /** Texto sem a citação, usado quando não há HTML. */
  texto?: string | null;
  destinatarios?: { to?: string[]; cc?: string[] } | null;
}

/** Detecta o tema pela classe que o Tailwind usa (`darkMode: 'class'`). */
function usarTemaEscuro(): boolean {
  const [escuro, setEscuro] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const alvo = document.documentElement;
    const obs = new MutationObserver(() => setEscuro(alvo.classList.contains('dark')));
    obs.observe(alvo, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return escuro;
}

export default function CorpoEmail({ assunto, html, texto, destinatarios }: Props) {
  const [carregarRemotas, setCarregarRemotas] = useState(false);
  const escuro = usarTemaEscuro();

  const temRemotas = useMemo(() => temImagemRemota(html ?? ''), [html]);
  const documento = useMemo(
    () => (html ? montarDocumento(html, carregarRemotas, escuro) : ''),
    [html, carregarRemotas, escuro],
  );

  const copia = destinatarios?.cc?.filter(Boolean) ?? [];

  return (
    <div className="flex flex-col gap-1.5">
      {assunto && (
        <p className="flex items-start gap-1.5 text-sm font-semibold text-gray-800 dark:text-slate-100">
          <Mail size={14} className="mt-0.5 shrink-0 text-gray-400" />
          <span className="break-words">{assunto}</span>
        </p>
      )}

      {copia.length > 0 && (
        // Quem mais está na conversa. Sem isto, o atendente responde achando que
        // é conversa de dois e o cliente recebe com três pessoas em cópia.
        <p className="text-[11px] text-gray-500 dark:text-slate-400">
          Cópia: {copia.join(', ')}
        </p>
      )}

      {html ? (
        <>
          {temRemotas && !carregarRemotas && (
            <button
              type="button"
              onClick={() => setCarregarRemotas(true)}
              className="flex w-fit items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/50"
              title="Imagens externas avisam o remetente de que você abriu a mensagem"
            >
              <ImageOff size={12} />
              Imagens bloqueadas — carregar
            </button>
          )}

          {/* A altura é fixa com rolagem própria, e não ajustada ao conteúdo:
              medir o conteúdo exigiria script rodando dentro do quadro, que é
              justamente o que o sandbox proíbe. Preferimos a rolagem. */}
          <iframe
            sandbox=""
            srcDoc={documento}
            title={assunto || 'Conteúdo do e-mail'}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="h-[420px] w-full rounded-lg border border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-800"
          />
        </>
      ) : (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
          {texto || <span className="italic text-gray-400">mensagem sem conteúdo</span>}
        </p>
      )}
    </div>
  );
}
